import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { QueryState } from '../components/QueryState'
import { useAdminMe } from '../components/RequireAdmin'
import { makeLabeler, useCommonCodes } from '../lib/codes'
import { adminApi, can, type AdminProblemCard } from '../lib/adminApi'
import { BoolSettingNotice } from './AdminReviewPage'

/**
 * 매칭 관리 — 문제카드를 골라 매칭을 돌리는 입구.
 *
 * **배점을 화면 맨 위에 보여준다.** 지금 나오는 점수가 어떤 설정으로 계산된 것인지
 * 운영자가 결과를 보기 전에 알아야 한다(과업지시서 §4-라의 가중치 조정 기능은
 * 이 값이 눈에 보일 때만 의미가 있다).
 */

const SCORING_ITEM = 'scoring_item'

export function AdminMatchPage() {
  const me = useAdminMe()
  const [q, setQ] = useState('')
  const problems = useQuery({
    queryKey: ['admin-match-problems', q],
    queryFn: () => adminApi.problemCards({ q, limit: 100 }),
  })
  const items: AdminProblemCard[] = problems.data?.items ?? []

  return (
    <div>
      <div className="admin-bar">
        <div>
          <h1 className="page-title">매칭</h1>
          <p className="page-desc">
            문제카드가 공개되면 매칭은 자동으로 실행됩니다. 여기서는 결과를 검토하거나 배점을
            바꿔 다시 돌립니다 — 실행 결과는 <b>결과 공개 승인</b>(또는 아래 자동 공개 스위치)을
            거쳐야 공개 화면에 나갑니다.
          </p>
        </div>
        <div className="admin-who">
          <Link className="btn ghost" to="/admin/match/by-card">
            해결카드 기준
          </Link>
          <Link className="btn ghost" to="/admin/match/unmet">
            미충족 큐
          </Link>
        </div>
      </div>

      <MatchAutoPublishNotice mayManage={can(me.data, 'admin', 'manage')} />

      <WeightPanel />

      <div className="filters">
        <label htmlFor="match-problem-q" className="muted small">
          문제 검색
        </label>
        <input
          id="match-problem-q"
          type="search"
          value={q}
          placeholder="제목으로 찾기"
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <QueryState
        isPending={problems.isPending}
        error={problems.error}
        isEmpty={items.length === 0}
      >
        <table className="admin-table">
          <thead>
            <tr>
              <th>문제</th>
              <th>지역</th>
              <th>검수 상태</th>
              <th>접수일</th>
              <th>매칭</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id}>
                <td>
                  <b>{c.title}</b>
                </td>
                <td>{c.region.name}</td>
                <td>{c.reviewStatusLabel}</td>
                <td className="muted small">{c.createdAt.slice(0, 10)}</td>
                <td>
                  <Link className="btn ghost small-btn" to={`/admin/match/${c.id}`}>
                    결과 보기
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
    </div>
  )
}

/** 매칭 결과 자동 공개(08-27 확정 모델) — 공개를 가르는 스위치는 이것 하나다(실행은 항상 자동). */
function MatchAutoPublishNotice(props: { mayManage: boolean }) {
  return (
    <BoolSettingNotice
      mayManage={props.mayManage}
      queryKey="match-auto-publish"
      read={adminApi.matchAutoPublish}
      write={adminApi.setMatchAutoPublish}
      title={(on) => `매칭 결과 자동 공개 ${on ? '켜짐' : '꺼짐'}`}
      description="켜져 있으면 자동 실행된 매칭 결과가 즉시 공개 화면에 나갑니다. 꺼져 있으면 결과는 미공개로 쌓이고, 배점 조정·재실행 후 결과 화면에서 공개를 승인해야 나갑니다."
    />
  )
}

/**
 * 활성 배점 1건. **값은 아직 정본이 아니다**(확정요청서 B-1) — 그 사실을 화면에 적어
 * 운영자가 지금 점수를 확정된 기준으로 오해하지 않게 한다.
 */
export function WeightPanel() {
  // 같은 쿼리 키라 결과 화면과 캐시를 공유한다 — 두 곳에서 써도 요청은 한 번이다.
  const query = useQuery({
    queryKey: ['admin-weight-config'],
    queryFn: () => adminApi.activeWeightConfig(),
  })
  const codes = useCommonCodes(SCORING_ITEM)
  const label = makeLabeler(codes.data)
  const config = query.data

  if (query.error !== null) {
    return (
      <div className="notice error" role="alert">
        활성 배점 설정을 읽지 못했습니다. 설정이 없으면 매칭을 실행할 수 없습니다.
      </div>
    )
  }
  if (config === undefined) return <div className="panel muted small">배점을 불러오는 중…</div>

  return (
    <div className="panel">
      <div className="weight-head">
        <b>배점 {config.version}</b>
        <span className="muted small">
          알고리즘 {config.algorithmVersion} · 등급 경계 중간 {config.gradeMidMin}점 · 높음{' '}
          {config.gradeHighMin}점
        </span>
        <Link className="btn ghost small-btn" to="/admin/weights">
          배점 조정
        </Link>
      </div>
      <ul className="weight-list">
        {config.items.map((i) => (
          <li key={i.scoringItem}>
            <span>{label(i.scoringItem)}</span>
            <b>{i.weight}</b>
          </li>
        ))}
      </ul>
      <p className="muted small">
        배점은 코드가 아니라 설정 테이블에서 읽습니다. 값을 바꾸려면 <b>배점 조정</b>에서 새
        배점을 만들어 적용하고, 되돌리려면 예전 배점을 다시 적용합니다 — 지난 실행 결과는
        그때의 배점으로 그대로 남습니다.
      </p>
    </div>
  )
}
